// src/components/RoadmapBadges.jsx
import React, { useMemo } from "react";
import { getChapterStars } from "../utils/stars";

// Stars begin at Chapter 2 (→ last is Chapter 9).
const FIRST = 2;
const LAST = 9;

/**
 * Approximate badge positions as percentages over /images/map/overworld.png.
 * Tweak these to match your art precisely.
 */
const POS = {
  2: { x: 20,  y: 63 }, // left lower path
  3: { x: 22,  y: 30 }, // penguin area
  4: { x: 43,  y: 24 }, // top lake-left
  5: { x: 79,  y: 18 }, // top right (reindeer zone)
  6: { x: 90,  y: 48 }, // spider/cocoon area
  7: { x: 87,  y: 90 }, // peacock bottom-right
  8: { x: 92,  y: 66 }, // panda forest mid-right
  9: { x: 66,  y: 65 }, // croc river mid
};

/** Simple 5-point star svg */
function StarGlyph({ size = 18, filled }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <path
        d="M12 2l3.09 6.3 6.95 1.01-5.02 4.88 1.18 6.9L12 18.77 5.8 21.09 6.98 14.2 1.96 9.31l6.95-1.01L12 2z"
        fill={filled ? "#F6C441" : "#132434"}
        stroke={filled ? "#C99100" : "#7EC3FF"}
        strokeWidth="1.6"
      />
    </svg>
  );
}

/**
 * Stars-only overlay for the roadmap.
 * - Gold star for collected chapters (2..9).
 * - Blue-outlined star for not-yet-collected.
 * - Subtle halo for the next stop and a purple outline for the current chapter.
 * - Visual only (pointer-events: none) so underlying dots/buttons remain clickable.
 */
export default function RoadmapBadges({ currentChapter }) {
  const collectedMap = useMemo(() => {
    const map = {};
    for (let ch = FIRST; ch <= LAST; ch += 1) map[ch] = getChapterStars(ch) >= 1;
    return map;
  }, []);

  const nextStop = useMemo(() => {
    for (let ch = FIRST; ch <= LAST; ch += 1) if (!collectedMap[ch]) return ch;
    return null;
  }, [collectedMap]);

  return (
    <div className="absolute inset-0 pointer-events-none select-none">
      {Object.entries(POS).map(([chStr, { x, y }]) => {
        const ch = Number(chStr);
        const isCollected = collectedMap[ch];
        const isActive = ch === nextStop;
        const isCurrent = ch === currentChapter;

        const ringColor = isCollected ? "#EEB300" : "#7EC3FF";
        const halo = isActive ? "0 0 0 6px rgba(255,221,0,0.28)" : "0 2px 6px rgba(0,0,0,.25)";
        const outline = isCurrent ? "2px solid #8b5cf6" : "none";

        return (
          <div
            key={ch}
            className="absolute flex items-center justify-center"
            style={{
              left: `${x}%`,
              top: `${y}%`,
              transform: "translate(-50%,-50%)",
              width: 32,
              height: 32,
              borderRadius: 999,
              background: "rgba(255,255,255,0.85)",
              border: `2px solid ${ringColor}`,
              boxShadow: halo,
              outline,
            }}
          >
            <StarGlyph size={18} filled={isCollected} />
          </div>
        );
      })}
    </div>
  );
}
